import React from "react";
import { Home, MapPin } from "lucide-react";
import type Property from "@/model/interface/Property";
import { formatNumber, formatWholeDollars } from "@/util/sums";
import { mapSearchUrl } from "@/util/maps";

const MAX_PROPERTIES = 2;

interface LeadPropertyCardsProps {
  properties: Property[];
}

export function LeadPropertyCards({ properties }: LeadPropertyCardsProps) {
  if (properties.length === 0) {
    return null;
  }

  return (
    <div className="flex flex-wrap gap-2">
      {properties.slice(0, MAX_PROPERTIES).map((property) => {
        const locality = [property.city, [property.state, property.zip].filter(Boolean).join(" ")]
          .filter(Boolean)
          .join(", ");
        const fullAddress = [property.address, locality].filter(Boolean).join(", ");
        const facts = [
          property.beds != null ? `${property.beds} bd` : null,
          property.baths != null ? `${property.baths} ba` : null,
          property.sqft ? `${formatNumber(property.sqft, 0, true)} sqft` : null,
        ]
          .filter(Boolean)
          .join(" · ");
        const value = property.marketValue ?? property.taxAssessedValue;
        return (
          <div key={property.id} className="min-w-[260px] flex-1 rounded-lg border border-border px-4 py-3">
            <div className="flex items-start gap-2">
              <Home className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
              <div className="min-w-0 flex-1">
                <div className="truncate text-base font-semibold">{property.address}</div>
                {locality && <div className="truncate text-sm text-muted-foreground">{locality}</div>}
              </div>
              <a
                href={mapSearchUrl(fullAddress)}
                target="_blank"
                rel="noreferrer"
                className="flex shrink-0 items-center gap-1 text-sm text-primary hover:underline"
              >
                <MapPin className="h-3.5 w-3.5" />
                Map
              </a>
            </div>
            <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-sm">
              {facts && <span className="text-muted-foreground">{facts}</span>}
              {value != null && (
                <span>
                  Value <b className="font-semibold">{formatWholeDollars(value)}</b>
                </span>
              )}
              {property.equityEstimate != null && (
                <span>
                  Equity <b className="font-semibold">{formatWholeDollars(property.equityEstimate)}</b>
                </span>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
